import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Plus, Edit, Trash2, MessageSquare, Video, Eye, Filter } from 'lucide-react';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import apiService from '../../services/api';
import '../../styles/UnifiedTestimonials.css';

const UnifiedTestimonials = ({ user }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [filteredTestimonials, setFilteredTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [deletingTestimonial, setDeletingTestimonial] = useState(null);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    company: '',
    content: '',
    image: '',
    video_url: '',
    type: 'text',
    rating: 5,
    status: 'active'
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  useEffect(() => {
    filterTestimonials();
  }, [testimonials, typeFilter]);

  const fetchTestimonials = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        apiService.setToken(token);
      }
      const data = await apiService.getTestimonials();
      setTestimonials(data);
    } catch (error) {
      toast.error('Failed to fetch testimonials');
    } finally {
      setLoading(false);
    }
  };

  const filterTestimonials = () => {
    if (typeFilter === 'all') {
      setFilteredTestimonials(testimonials);
    } else {
      setFilteredTestimonials(testimonials.filter(t => t.type === typeFilter));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (token) {
        apiService.setToken(token);
      }

      if (editingTestimonial) {
        await apiService.updateTestimonial(editingTestimonial.id, formData);
      } else {
        await apiService.createTestimonial(formData);
      }

      toast.success(`Testimonial ${editingTestimonial ? 'updated' : 'created'} successfully`);
      resetModal();
      fetchTestimonials();
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const resetModal = () => {
    setShowModal(false);
    setEditingTestimonial(null);
    setFormData({
      name: '',
      designation: '',
      company: '',
      content: '',
      image: '',
      video_url: '',
      type: 'text',
      rating: 5,
      status: 'active'
    });
  };

  const handleView = (testimonial) => {
    setSelectedTestimonial(testimonial);
    setShowViewModal(true);
  };

  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData(testimonial);
    setShowModal(true);
  };

  const handleDelete = (testimonial) => {
    setDeletingTestimonial(testimonial);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        apiService.setToken(token);
      }
      await apiService.deleteTestimonial(deletingTestimonial.id);
      toast.success('Testimonial deleted successfully');
      fetchTestimonials();
    } catch (error) {
      toast.error(error.message || 'Failed to delete testimonial');
    } finally {
      setShowDeleteModal(false);
      setDeletingTestimonial(null);
    }
  };

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'designation', label: 'Designation' },
    { key: 'company', label: 'Company' },
    {
      key: 'type',
      label: 'Type',
      render: (testimonial) => (
        <span className={`badge ${testimonial.type === 'video' ? 'video' : 'text'}`}>
          {testimonial.type === 'video' ? <Video size={12} /> : <MessageSquare size={12} />}
          {testimonial.type}
        </span>
      )
    },
    {
      key: 'rating',
      label: 'Rating',
      render: (testimonial) => (
        <span>{'★'.repeat(testimonial.rating || 0)} ({testimonial.rating || 0})</span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (testimonial) => (
        <span className={`status ${testimonial.status}`}>
          {testimonial.status}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (testimonial) => (
        <div className="action-buttons">
          <button onClick={() => handleView(testimonial)} className="btn-sm" title="View">
            <Eye size={16} />
          </button>
          <button onClick={() => handleEdit(testimonial)} className="btn-edit" title="Edit">
            <Edit size={16} />
          </button>
          <motion.button
            className="btn-sm danger touch-target"
            onClick={() => handleDelete(testimonial)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Delete"
          >
            <Trash2 size={12} />
          </motion.button>
        </div>
      )
    }
  ];

  return (
    <div className="testimonials-management">
      <div className="page-header">
        <h2><MessageSquare size={24} /> Testimonials Management</h2>
        <div className="header-actions">
          <div className="filter-group">
            <Filter size={16} />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Types</option>
              <option value="text">Text Only</option>
              <option value="video">Video Only</option>
            </select>
          </div>
          <motion.button
            className="btn-primary"
            onClick={() => setShowModal(true)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Plus size={16} /> Add Testimonial
          </motion.button>
        </div>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <h3>{testimonials.filter(t => t.type === 'text').length}</h3>
          <p>Text Testimonials</p>
        </div>
        <div className="stat-card">
          <h3>{testimonials.filter(t => t.type === 'video').length}</h3>
          <p>Video Testimonials</p>
        </div>
        <div className="stat-card">
          <h3>{testimonials.filter(t => t.status === 'active').length}</h3>
          <p>Active</p>
        </div>
      </div>

      <DataTable
        data={filteredTestimonials}
        columns={columns}
        loading={loading}
        emptyMessage={`No ${typeFilter === 'all' ? '' : typeFilter + ' '}testimonials found`}
      />

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={resetModal}
        title={`${editingTestimonial ? 'Edit' : 'Add'} Testimonial`}
      >
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Type *</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                <option value="text">Text Testimonial</option>
                <option value="video">Video Testimonial</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Designation</label>
              <input
                type="text"
                value={formData.designation}
                onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Company</label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Content *</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows="4"
              required
              placeholder={formData.type === 'video' ? 'Brief description or transcript...' : 'Testimonial content...'}
            />
          </div>

          {formData.type === 'text' && (
            <div className="form-group">
              <label>Image URL</label>
              <input
                type="url"
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
            </div>
          )}

          {formData.type === 'video' && (
            <div className="form-group">
              <label>Video URL *</label>
              <input
                type="url"
                value={formData.video_url}
                onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                required
                placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
              />
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Rating</label>
              <select
                value={formData.rating}
                onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
              >
                {[1, 2, 3, 4, 5].map(num => (
                  <option key={num} value={num}>{num} Star{num > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">
              {editingTestimonial ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Testimonial Details"
      >
        {selectedTestimonial && (
          <div className="testimonial-details">
            <div className="detail-header">
              <span className={`badge ${selectedTestimonial.type}`}>
                {selectedTestimonial.type === 'video' ? <Video size={16} /> : <MessageSquare size={16} />}
                {selectedTestimonial.type} Testimonial
              </span>
              <span className={`status ${selectedTestimonial.status}`}>
                {selectedTestimonial.status}
              </span>
            </div>

            <div className="form-group">
              <label>Name</label>
              <p>{selectedTestimonial.name}</p>
            </div>

            {selectedTestimonial.designation && (
              <div className="form-group">
                <label>Designation</label>
                <p>{selectedTestimonial.designation}</p>
              </div>
            )}

            {selectedTestimonial.company && (
              <div className="form-group">
                <label>Company</label>
                <p>{selectedTestimonial.company}</p>
              </div>
            )}

            <div className="form-group">
              <label>Content</label>
              <p style={{ whiteSpace: 'pre-wrap' }}>{selectedTestimonial.content}</p>
            </div>

            {selectedTestimonial.type === 'text' && selectedTestimonial.image && (
              <div className="form-group">
                <label>Image</label>
                <img src={selectedTestimonial.image} alt={selectedTestimonial.name} style={{ maxWidth: '200px', borderRadius: '8px' }} />
              </div>
            )}

            {selectedTestimonial.type === 'video' && selectedTestimonial.video_url && (
              <div className="form-group">
                <label>Video URL</label>
                <a href={selectedTestimonial.video_url} target="_blank" rel="noopener noreferrer">
                  {selectedTestimonial.video_url}
                </a>
              </div>
            )}

            <div className="form-group">
              <label>Rating</label>
              <p>{'★'.repeat(selectedTestimonial.rating || 0)} ({selectedTestimonial.rating || 0}/5)</p>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletingTestimonial(null);
        }}
        title="Confirm Delete"
      >
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <p>Are you sure you want to delete this testimonial?</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1.5rem' }}>
            <button className="btn-sm danger" onClick={confirmDelete}>
              Delete
            </button>
            <button
              className="btn-sm"
              onClick={() => {
                setShowDeleteModal(false);
                setDeletingTestimonial(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UnifiedTestimonials;